import { pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { users } from './users.ts';

/**
 * Tabela `admin_invites` — PRÉ-AUTORIZAÇÃO de administradores por e-mail.
 *
 * O fluxo de usuário nasce no primeiro login SSO (espelho do Clerk em `users`),
 * então não existe linha em `users` para quem ainda não entrou — e é exatamente
 * esse o caso que esta tabela cobre: um admin registra o e-mail ANTES, e quando
 * a conta é criada com ele o usuário já nasce `role='admin'` (a checagem vive
 * no provisionamento do primeiro login, junto do SUPER_ADMIN_EMAIL).
 *
 * O registro NÃO é apagado ao ser consumido: `consumedAt`/`consumedByUserId`
 * viram trilha de auditoria de quem autorizou e quem herdou. A promoção só
 * acontece na CRIAÇÃO da conta — consumido não promove de novo (rebaixar um
 * admin depois é decisão da tela de usuários e o convite antigo não pode
 * ressuscitá-la a cada login).
 *
 * Para quem JÁ tem conta a rota recusa o convite e aponta o caminho certo
 * ("Tornar admin" na lista): um único mecanismo por situação, sem promoção
 * surpresa por fora da tela.
 */
export const adminInvites = pgTable(
  'admin_invites',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    // Sempre minúsculo e sem espaços (o Zod abaixo normaliza): o e-mail do SSO
    // chega em caixa variada e a comparação do provisionamento é exata.
    email: text('email').notNull(),
    // Quem pré-autorizou — trilha de auditoria. `restrict` de propósito: users
    // não são apagados neste app (desativação é status), e se um dia alguém
    // tentar, o convite denuncia a dependência em vez de sumir em silêncio.
    createdByUserId: uuid('created_by_user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    // Preenchidos quando a conta nasce e herda admin. `set null` no consumidor:
    // se a conta um dia sair, o registro do convite consumido permanece.
    consumedAt: timestamp('consumed_at', { withTimezone: true }),
    consumedByUserId: uuid('consumed_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
  },
  (t) => [uniqueIndex('admin_invites_email_idx').on(t.email)],
);

export type AdminInvite = typeof adminInvites.$inferSelect;
export type NewAdminInvite = typeof adminInvites.$inferInsert;

export const adminInviteSelectSchema = createSelectSchema(adminInvites);

/**
 * Payload de criação — só o e-mail entra pelo cliente (quem autoriza sai do
 * token; datas e consumo são do servidor). Normaliza para minúsculo/aparado
 * ANTES de validar: é a forma gravada e comparada no provisionamento.
 */
export const adminInviteCreateSchema = createInsertSchema(adminInvites, {
  email: (s) => s.trim().toLowerCase().email('E-mail inválido'),
}).pick({ email: true });

export type AdminInviteCreateInput = typeof adminInviteCreateSchema._output;
